/**
 * BundleDetailPage — detailed view of one bundle.
 *
 * Shows:
 * - Bundle identity
 * - Sub-modules list (loaded from the bundle's bundle-manifest.json)
 * - Consumers
 */

import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useBundle } from '../state/gallery-store';
import './BundleDetailPage.css';

interface BundleManifest {
  bundle: { id: string; name: string; description: string };
  version: string;
  submodules: Array<{
    id: string;
    version: string;
    visibility: 'public' | 'private';
    optional: boolean;
    feature: string;
    note?: string;
  }>;
  compatibility: {
    fvw: string;
    platform: string[];
    node: string;
  };
  license: {
    type: string;
    name: string;
    requires_key: boolean;
  };
}

export function BundleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const bundle = useBundle(id ?? null);
  const [manifest, setManifest] = useState<BundleManifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [manifestLoading, setManifestLoading] = useState(true);

  useEffect(() => {
    if (!bundle) return;
    setManifestLoading(true);
    setManifestError(null);
    // Try the live manifest first
    const liveUrl = `https://raw.githubusercontent.com/avidtech6/fv-module-gallery/main/bundles/${bundle.id}/bundle-manifest.json`;
    fetch(liveUrl)
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error(`HTTP ${r.status}`);
      })
      .then((data: BundleManifest) => {
        setManifest(data);
        setManifestLoading(false);
      })
      .catch(() => {
        // Fall back to bundled copy
        fetch(`/bundles/${bundle.id}/bundle-manifest.json`)
          .then((r) => {
            if (r.ok) return r.json();
            throw new Error(`HTTP ${r.status}`);
          })
          .then((data: BundleManifest) => {
            setManifest(data);
            setManifestLoading(false);
          })
          .catch((e) => {
            setManifestError(e instanceof Error ? e.message : String(e));
            setManifestLoading(false);
          });
      });
  }, [bundle]);

  if (!bundle) {
    return (
      <div>
        <p>Bundle not found: <code>{id}</code></p>
        <Link to="/bundles">← Back to bundles</Link>
      </div>
    );
  }

  return (
    <div className="bundle-detail">
      <Link to="/bundles" className="bundle-detail__back">
        ← All bundles
      </Link>

      <header className="bundle-detail__header">
        <h1 className="bundle-detail__id">{bundle.id}</h1>
        <div className="bundle-detail__meta">
          <span className={`bundle-detail__tier bundle-detail__tier--${bundle.tier}`}>
            {bundle.tier}
          </span>
          <span className="bundle-detail__version">v{bundle.version}</span>
        </div>
      </header>

      {manifest && (
        <>
          <p className="bundle-detail__description">{manifest.bundle.description}</p>

          <section className="bundle-detail__section">
            <h2 className="bundle-detail__section-title">License</h2>
            <dl className="bundle-detail__dl">
              <dt>Type</dt>
              <dd><code>{manifest.license.type}</code></dd>
              <dt>Name</dt>
              <dd><code>{manifest.license.name}</code></dd>
              <dt>Requires key</dt>
              <dd>{manifest.license.requires_key ? 'Yes' : 'No'}</dd>
            </dl>
          </section>

          <section className="bundle-detail__section">
            <h2 className="bundle-detail__section-title">Compatibility</h2>
            <dl className="bundle-detail__dl">
              <dt>FVW</dt>
              <dd><code>{manifest.compatibility.fvw}</code></dd>
              <dt>Platforms</dt>
              <dd>{manifest.compatibility.platform.map((p) => (
                <code key={p}>{p}</code>
              ))}</dd>
              <dt>Node</dt>
              <dd><code>{manifest.compatibility.node}</code></dd>
            </dl>
          </section>

          <section className="bundle-detail__section">
            <h2 className="bundle-detail__section-title">
              Sub-modules ({manifest.submodules.length})
            </h2>
            <table className="bundle-detail__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Version</th>
                  <th>Visibility</th>
                  <th>Feature</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {manifest.submodules.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/modules/${s.id}`}>{s.id}</Link>
                    </td>
                    <td><code>{s.version}</code></td>
                    <td>
                      <span className={`bundle-detail__vis bundle-detail__vis--${s.visibility}`}>
                        {s.visibility}
                      </span>
                    </td>
                    <td>{s.feature}</td>
                    <td>{s.optional ? 'optional' : 'required'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {manifestLoading && <p>Loading manifest…</p>}
      {manifestError && (
        <p className="bundle-detail__error">
          Failed to load bundle manifest: {manifestError}
        </p>
      )}

      <div className="bundle-detail__actions">
        <button
          type="button"
          className="bundle-detail__btn bundle-detail__btn--primary"
          onClick={() => alert('Install bundle: API not yet available')}
        >
          Install bundle
        </button>
      </div>
    </div>
  );
}
